import { useState, type CSSProperties } from 'react';
import type { Book } from '@booknest/shared';

import {
  pickColors,
  pickTextureBase,
} from './bookcase/spine-utils.js';

interface BookCoverProps {
  url: string | null;
  title: string;
  /**
   * Book (id + genre) pour générer une couverture stylisée en fallback —
   * gradient + titre doré façon maquette. Si absent, fallback générique 📕.
   */
  book?: Pick<Book, 'id' | 'genre'>;
  /**
   * Contrôle la taille externe (ex: `w-10`, `w-20`).
   * L'image remplit toujours 100% du conteneur, qui a un aspect 2/3.
   */
  className?: string;
}

export function BookCover({
  url,
  title,
  book,
  className = '',
}: BookCoverProps): JSX.Element {
  // Fallback automatique : si l'URL renvoie un 404 (typique d'OL Covers
  // sollicité en `?default=false` quand le livre n'a pas de cover indexée),
  // on retombe sur la version stylisée.
  const [loadFailed, setLoadFailed] = useState(false);
  const showImage = url !== null && !loadFailed;
  return (
    <div
      className={`aspect-[2/3] flex-shrink-0 overflow-hidden rounded-xl ${className}`}
    >
      {showImage ? (
        <img
          src={url}
          alt={`Couverture de ${title}`}
          loading="lazy"
          onError={() => setLoadFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : book ? (
        <StyledCover title={title} book={book} />
      ) : (
        <div
          aria-label={`Couverture de ${title}`}
          className="flex h-full w-full items-center justify-center bg-cream-dark text-2xl text-brown-soft"
        >
          📕
        </div>
      )}
    </div>
  );
}

interface StyledCoverProps {
  title: string;
  book: Pick<Book, 'id' | 'genre'>;
}

/**
 * Cover stylisée : gradient cuir (couleurs cohérentes avec la spine du même
 * livre) + titre en doré. Effet "vieux livre relié" de la maquette.
 */
function StyledCover({ title, book }: StyledCoverProps): JSX.Element {
  const [c1, c2] = pickColors(book);
  const textureBase = pickTextureBase(book);
  const cssVars: CSSProperties = {
    ['--c1' as string]: c1,
    ['--c2' as string]: c2,
  };
  return (
    <div
      aria-label={`Couverture stylisée de ${title}`}
      className={`${textureBase} flex h-full w-full items-center justify-center px-2 py-3`}
      style={cssVars}
    >
      <p
        className="line-clamp-4 text-center font-serif text-[11px] font-bold leading-tight"
        style={{
          color: 'rgba(255, 220, 150, 0.95)',
          textShadow: '0 0 4px rgba(218,165,32,0.65), 0 1px 2px rgba(0,0,0,0.5)',
        }}
      >
        {title}
      </p>
    </div>
  );
}
